import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as fileApi from '@/api/file';
import { Spin, Table, Tabs } from 'antd';
import type { ColumnType } from 'antd/es/table';

interface ExcelViewerProps {
    filePath?: string;
}

interface SheetData {
    name: string;
    columns: ColumnType<Record<string, string>>[];
    rows: Record<string, string>[];
    scrollX: number;
}

const COL_WIDTH = 120;
const TABS_HEADER_HEIGHT = 46;

const ExcelViewer: React.FC<ExcelViewerProps> = ({ filePath }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [sheets, setSheets] = useState<SheetData[]>([]);
    const observerRef = useRef<ResizeObserver | null>(null);
    const [tableHeight, setTableHeight] = useState(400);

    const containerRef = useCallback((el: HTMLDivElement | null) => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height ?? 0;
            setTableHeight(Math.max(200, height - TABS_HEADER_HEIGHT));
        });
        observer.observe(el);
        observerRef.current = observer;
    }, []);

    useEffect(() => {
        if (!filePath) return;
        setLoading(true);
        setError(undefined);
        setSheets([]);

        let worker: Worker | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const cleanup = () => {
            if (timeoutId !== null) clearTimeout(timeoutId);
            worker?.terminate();
        };

        const parse = async () => {
            try {
                const data = await fileApi.getRawFileResourceInBlob(filePath);
                if (!data) throw new Error('Failed to load file');
                const arrayBuffer = await new Blob([data]).arrayBuffer();

                const parsed = await new Promise<SheetData[]>((resolve, reject) => {
                    worker = new Worker(
                        new URL('../workers/excelParser.worker.ts', import.meta.url),
                        { type: 'module' },
                    );

                    // Kill the worker and surface an error if parsing takes too long
                    // (e.g. corrupted file causing an infinite loop in XLSX.read)
                    timeoutId = setTimeout(() => {
                        worker?.terminate();
                        reject(new Error('Parse timeout: file may be corrupted or too large.'));
                    }, 30_000);

                    worker.onmessage = (e) => {
                        clearTimeout(timeoutId!);
                        const result = e.data;
                        if (!result.success) {
                            reject(new Error(result.error));
                            return;
                        }
                        const sheets: SheetData[] = result.sheets.map(
                            ({ name, jsonRows }: { name: string; jsonRows: string[][] }) => {
                                if (!jsonRows.length) {
                                    return { name, columns: [], rows: [], scrollX: 0 };
                                }
                                const headerRow = jsonRows[0];
                                const columns: ColumnType<Record<string, string>>[] = headerRow.map(
                                    (col, idx) => {
                                        const key = String(idx);
                                        return { title: col || `Col ${idx + 1}`, dataIndex: key, key };
                                    },
                                );
                                const rows: Record<string, string>[] = jsonRows
                                    .slice(1)
                                    .map((row, rowIdx) => {
                                        const record: Record<string, string> = {
                                            _rowKey: String(rowIdx),
                                        };
                                        headerRow.forEach((_, colIdx) => {
                                            record[String(colIdx)] = String(row[colIdx] ?? '');
                                        });
                                        return record;
                                    });
                                return { name, columns, rows, scrollX: headerRow.length * COL_WIDTH };
                            },
                        );
                        resolve(sheets);
                    };

                    worker.onerror = (e) => {
                        clearTimeout(timeoutId!);
                        reject(new Error(e.message));
                    };

                    worker.postMessage(arrayBuffer, [arrayBuffer]);
                });

                setSheets(parsed);
            } catch {
                setError('Failed to parse Excel file. The file may be corrupted.');
            } finally {
                setLoading(false);
                cleanup();
            }
        };

        parse();

        return cleanup;
    }, [filePath]);

    if (loading) {
        return (
            <div className='w-full h-full flex items-center justify-center'>
                <Spin />
            </div>
        );
    }

    if (error) {
        return (
            <div className='w-full h-full flex items-center justify-center text-red-500 dark:text-[#F48771]'>{error}</div>
        );
    }

    const tabItems = sheets.map((sheet) => ({
        key: sheet.name,
        label: sheet.name,
        children: (
            <Table<Record<string, string>>
                virtual
                rowKey='_rowKey'
                columns={sheet.columns}
                dataSource={sheet.rows}
                scroll={{ x: sheet.scrollX, y: tableHeight }}
                pagination={false}
                size='small'
                bordered
            />
        ),
    }));



    return (
        <div ref={containerRef} className='w-full h-full overflow-hidden flex flex-col'>
            <Tabs
                items={tabItems}
                size='small'
                className='flex-1 overflow-hidden'
            />
        </div>
    );
};

export default ExcelViewer;
